import { ReactNode, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { LoadingState, ProgressInfo } from '@cartwhl/ui/feature/GenericLoader';
import { TimelineItem, TimelineProvider } from '@cartwhl/ui/feature/Timeline';
import Button from '@cartwhl/ui/input/Button';

import {
  AnimationMainPlayerNew,
  CameraButtons,
  FooterNew,
} from 'components/AnimationPlayer';
import { AnimationFrontDoor } from 'components/FrontDoor';
import { StudioLayout } from 'components/StudioLayout';
import { VideoOverlay } from 'components/Timeline';
import { animationPlayerStore, navigationStore, projectStore } from 'stores';

export default function Motion() {
  const { projectID } = navigationStore.getRouteParams();
  const playerStore = animationPlayerStore.defaultInstance;

  const {
    motion: { timelineData, stitching },
  } = useSnapshot(projectStore.state);

  const { isPending, loadingState } = usePendingState();

  const hasTimelineItems = Object.values(timelineData ?? {}).length > 0;

  useEffect(() => {
    projectStore.loadCurrentProject(projectID);
    projectStore.loadCurrentProjectGenerationHistory(projectID);
    return projectStore.resetCurrentProject;
  }, [projectID]);

  const handleOnChange = (timelineId: string, items: TimelineItem[]) => {
    console.log('handleOnChange', timelineId, items);
    projectStore.setMotionTimelineData({ [timelineId]: items });
  };

  const handleOnDuplicate = async (timelineId: string, index: number) => {
    console.log('handleOnDuplicate', index);
    if (!timelineData) return;
    const timelineItems = timelineData[timelineId];
    if (!timelineItems) return;
    const itemToDuplicate = timelineItems[index];

    // Add from existing
    await projectStore.addExistingMotionToProject({
      createAsNewProject: false,
      existingJobID: itemToDuplicate.jobID,
      existingVariationNumber: itemToDuplicate.variationNumber ?? 0,
      trimStart: itemToDuplicate.trimStart ?? undefined,
      trimEnd: itemToDuplicate.trimEnd ?? undefined,
      sequenceNumber: index + 1,
    });
  };

  const handleOnRemove = (timelineId: string, index: number) => {
    console.log('handleOnRemove', timelineId, index);
    if (!timelineData) return;
    const timelineItems = timelineData[timelineId];
    if (!timelineItems) return;

    // Aske user to confirm
    const confirmed = window.confirm(
      'Are you sure you want to remove this motion from the timeline?',
    );
    if (!confirmed) return;

    projectStore.setMotionTimelineData({
      [timelineId]: timelineItems.filter((_, i) => i !== index),
    });

    projectStore.triggerStitching(timelineId);
  };

  useEffect(() => {
    playerStore.setDisabled(isPending);
  }, [isPending, playerStore]);

  return (
    <TimelineProvider
      timelineData={timelineData}
      onChange={handleOnChange}
      onTriggerStitching={projectStore.triggerStitching}
      onDuplicate={handleOnDuplicate}
      onRemove={handleOnRemove}
      stitching={stitching}
      disabled={isPending}
    >
      <StudioLayout
        player={
          <AnimationMainPlayerNew showDefaultAnimations={!hasTimelineItems} />
        }
        cameraButtons={
          <CameraButtons animationPlayerStore={playerStore} variant="full" />
        }
        prompInput={<AnimationFrontDoor />}
        overlays={<VideoOverlay />}
        footer={<FooterNew />}
        loadingState={loadingState}
      />
    </TimelineProvider>
  );
}

/**
 * The idea here is to have one place that decides when to show loading state
 */
function usePendingState() {
  const {
    currentProject,
    currentProjectGenerationHistory,
    motion,
    meshLoadingStatus,
    mediaUpload,
  } = useSnapshot(projectStore.state);

  const isDataLoading =
    currentProject.status === 'pending' ||
    currentProjectGenerationHistory.status === 'pending';

  const isMeshLoading = meshLoadingStatus !== 'ready';
  const isMediaUploadPending =
    mediaUpload?.status === 'UPLOADING' ||
    mediaUpload?.status === 'QUEUED' ||
    mediaUpload?.status === 'PROCESSING';

  // Currently we only allow one pending job at a time
  const pendingJobId = currentProject.data?.pendingJobs?.[0] ?? undefined;
  const pendingJob = currentProjectGenerationHistory.data?.items?.find(
    (job) => job.jobID === pendingJobId,
  );
  const pendingStatus = pendingJob?.status;

  // isPending
  const isPending =
    Boolean(pendingJob) ||
    motion.addExistingMotionSubmitting ||
    isDataLoading ||
    isMeshLoading ||
    isMediaUploadPending;

  // text
  const text = isDataLoading
    ? 'Loading project data…'
    : isMeshLoading
      ? 'Loading character mesh…'
      : motion.addExistingMotionSubmitting
        ? 'Adding motion…'
        : mediaUpload?.status === 'UPLOADING'
          ? 'Uploading video…'
          : mediaUpload?.status === 'QUEUED'
            ? 'Upload queued…'
            : mediaUpload?.status === 'PROCESSING'
              ? 'Processing uploaded video…'
              : pendingStatus === 'QUEUED'
                ? 'Your request is queued…'
                : pendingStatus === 'GENERATING'
                  ? 'Generating animation…'
                  : pendingStatus === 'FAILED'
                    ? 'Animation generation failed. Please try again.'
                    : '';

  // progressInfo
  let progressInfo: ProgressInfo | undefined = undefined;
  if (
    currentProject.data?.type === 'MOTION' &&
    pendingStatus === 'GENERATING' &&
    pendingJob?.createdAt
  ) {
    progressInfo = {
      startedAt: new Date(pendingJob.createdAt).getTime(),
      estimatedProcessingTime: pendingJob.estimatedProcessingTime,
      onCancel: () => {
        if (pendingJobId) {
          projectStore.deleteJobFromBackend(pendingJobId);
        }
      },
    };
  }

  // buttons
  let buttons: ReactNode | undefined = undefined;
  if (pendingStatus === 'FAILED') {
    buttons = (
      <div className="mt-6 flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            if (pendingJobId) {
              projectStore.deleteJobFromBackend(pendingJobId);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            // TODO: Retry the job
            alert('Not implemented');
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const loadingState: LoadingState | undefined = isPending
    ? {
        text,
        type: pendingStatus === 'FAILED' ? 'FAILED' : 'PROCESSING',
        progressInfo,
        buttons,
      }
    : undefined;

  return {
    isPending,
    pendingJob,
    loadingState,
  };
}
